import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import type { ProblemDetailsUnaddressedMailingPositionError } from '@/services/ws-post/src/main/resources/rest-api';
import { isApiError } from '@/js/libs/api';
import dayjs from '@/js/libs/dayjs';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { useNotification } from '@/js/libs/ui/src/V2';

import type { RequestErrorNotification } from 'src/hooks/useRequestErrorNotification.ts';
import { useRequestErrorNotification } from 'src/hooks/useRequestErrorNotification.ts';

const UnaddressedMailingPositionErrorSchema = z.object({
  sortOrder: z.number(),
  positionName: z.string(),
  reservedByOrderId: z.string().optional(),
  translationKey: z.string(),
  reservedUnitIds: z.array(z.string()).optional(),
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
});

const UnaddressedMailingPositionsReconciliationErrorSchema = z.object({
  data: z.object({
    unaddressedMailingPositionErrors: z.array(UnaddressedMailingPositionErrorSchema).optional(),
  }),
  status: z.number().optional(),
});

const DELIVERY_UNITS_RESERVED_KEY = 'DELIVERY_UNITS_RESERVED';
const BACKEND_DATE_FORMAT = 'DD.MM.YYYY';
const DISPLAY_DATE_FORMAT = 'D MMMM YYYY';

const formatDate = (value?: string) => {
  if (!value) {
    return undefined;
  }

  const parsed = dayjs(value, BACKEND_DATE_FORMAT, true);
  return parsed.isValid() ? parsed.format(DISPLAY_DATE_FORMAT) : value;
};

export const formatReservedDateRange = (dateFrom?: string, dateTo?: string) => {
  const from = formatDate(dateFrom);
  const to = formatDate(dateTo);

  if (from && to) {
    return `${from} – ${to}`;
  }

  return from ?? to ?? '';
};

type DeliveryUnitsReservedHandler = (sortOrder: number, reservedUnitIds: string[]) => void;

type UnaddressedMailingPositionErrorsNotifier = (
  unaddressedMailingPositionErrors: ProblemDetailsUnaddressedMailingPositionError[],
  onDeliveryUnitsReserved?: DeliveryUnitsReservedHandler,
) => void;

type UnaddressedMailingPositionsApiErrorNotifier = (error: unknown, onDeliveryUnitsReserved?: DeliveryUnitsReservedHandler) => boolean;

/**
 * Shows one notification per failed unaddressed mailing position, for the `unaddressedMailingPositionErrors` of a failed response as well as
 * for the ones an asynchronous stage change reports in its status.
 * @returns A function that notifies about the given `unaddressedMailingPositionErrors`.
 */
export const useUnaddressedMailingPositionErrorsNotifier = (): UnaddressedMailingPositionErrorsNotifier => {
  const [t] = useTranslation(['postBookingTool']);
  const notify = useNotification();

  return useCallbackRef<UnaddressedMailingPositionErrorsNotifier>((unaddressedMailingPositionErrors, onDeliveryUnitsReserved) => {
    unaddressedMailingPositionErrors.forEach(
      ({ sortOrder, positionName, reservedByOrderId, translationKey, reservedUnitIds, dateFrom, dateTo }) => {
        if (translationKey === DELIVERY_UNITS_RESERVED_KEY) {
          if (reservedUnitIds?.length) {
            onDeliveryUnitsReserved?.(sortOrder, reservedUnitIds);
          }

          notify.error({
            title: positionName,
            body: t('postBookingTool:DELIVERY_UNITS_RESERVED', {
              dates: formatReservedDateRange(dateFrom, dateTo),
              reservedUnitIds: reservedUnitIds?.join(', ') ?? '',
              remoteMailingOrderId: reservedByOrderId?.toString() ?? '',
            }),
          });
          return;
        }

        notify.error({ title: positionName, body: t(`backend:${translationKey}`) });
      },
    );
  });
};

/**
 * Shows one notification per failed unaddressed mailing position for endpoints that reconcile positions with Promopost
 * (`PUT /post-campaigns/{id}`, `PATCH /post-campaigns/{id}/accept`, `PATCH .../positions/unaddressed-mailing/reconcile`).
 * @returns A function that notifies about `unaddressedMailingPositionErrors` and reports whether the error was of that shape.
 */
export const useUnaddressedMailingPositionErrorsNotification = (): UnaddressedMailingPositionsApiErrorNotifier => {
  const notifyUnaddressedMailingPositionErrors = useUnaddressedMailingPositionErrorsNotifier();

  return useCallbackRef<UnaddressedMailingPositionsApiErrorNotifier>((error, onDeliveryUnitsReserved) => {
    if (!isApiError(error, UnaddressedMailingPositionsReconciliationErrorSchema)) {
      return false;
    }

    const unaddressedMailingPositionErrors = error.response?.data.unaddressedMailingPositionErrors;

    if (!unaddressedMailingPositionErrors?.length) {
      return false;
    }

    notifyUnaddressedMailingPositionErrors(unaddressedMailingPositionErrors, onDeliveryUnitsReserved);

    return true;
  });
};

/**
 * `onFail` handler for requests that may fail per unaddressed mailing position: shows the per-position errors when the backend
 * sends them, and falls back to the default request error notification otherwise.
 */
export const useUnaddressedMailingRequestErrorNotification = (): RequestErrorNotification => {
  const notifyUnaddressedMailingPositionErrors = useUnaddressedMailingPositionErrorsNotification();
  const notifyRequestError = useRequestErrorNotification();

  return useCallbackRef<RequestErrorNotification>((apiError, isRetrying) => {
    if (notifyUnaddressedMailingPositionErrors(apiError)) {
      return;
    }

    notifyRequestError(apiError, isRetrying);
  });
};
